import React from "react";
import styled from "styled-components";
import { FieldConfig, FieldDataTypeEnum, FieldType, FormFieldTest, DEFAULT_BASIC_CONFIG } from "../../../types/types";
import StyledText from "../../StyledComponents/StyledText";
import RequiredIcon from "../../../assets/icons/RequiredIcon";
import { useTheme } from "../../../middleware/ThemeContext";
import FieldControl from "./FieldControl";
import { PhoneValidationOptions } from "../../../utils/phoneValidation";

interface FormFieldProps {
  formField: FormFieldTest;
  value: any;
  onChange: (value: any) => void;
  onBlur?: () => void;
  onValidationChange?: (error: string | null) => void;
  error?: string;
  uploadFolder?: string;
  forPreview?: boolean;
  onToggleRequired?: (isRequired: boolean) => void;
  phoneValidationOptions?: PhoneValidationOptions;
}

export const FormFieldControl: React.FC<FormFieldProps> = ({
  formField,
  value,
  onChange,
  onBlur,
  onValidationChange,
  error,
  uploadFolder,
  forPreview,
  onToggleRequired,
  phoneValidationOptions
}) => {
  const { theme } = useTheme();
  const config = formField?.field?.config as FieldConfig || DEFAULT_BASIC_CONFIG;
  const isDisplayOnly = formField?.field?.dataType === FieldDataTypeEnum.DISPLAY_ONLY;
  return (
    <FieldWrapper>
      {!isDisplayOnly && config && !config?.uiMeta?.hideLabel && (
        <FieldLabel theme={theme}>
          <span>
            {config?.uiMeta?.displayName || config?.uiMeta?.fieldLabel || formField?.field?.name}
            {formField.isRequired && <RequiredStar><RequiredIcon /></RequiredStar>}
          </span>
          {forPreview && onToggleRequired && (
            <ToggleWrapper onClick={(e) => e.stopPropagation()}>
              <div className="form-check form-switch m-0" onClick={(e) => e.stopPropagation()}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formField.isRequired || false}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleRequired(e.target.checked);
                  }}
                />
              </div>
              <ToggleLabel>Mandatory</ToggleLabel>
            </ToggleWrapper>
          )}
        </FieldLabel>
      )}
      <FieldControl
        field={formField.field}
        isRequired={formField.isRequired}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onValidationChange={onValidationChange}
        error={error}
        uploadFolder={uploadFolder}
        forPreview={forPreview}
        phoneValidationOptions={phoneValidationOptions}
      />
    </FieldWrapper>
  );
};

// Styled components (keep all your existing styled components exactly as they are)
const FieldWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const FieldLabel = styled(StyledText)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: 600,
  marginBottom: '4px',
  color: theme.CorporateTheme?.colors?.secondary,
  textTransform: 'capitalize',
}));

const RequiredStar = styled.span`
  margin-left: 2px;
  margin-top: -2px;
`;


const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.CorporateTheme?.colors?.textSecondary};
`;